import BaseRequest from './BaseRequest';
import HTTPRequest from '../HTTPRequest';
import { deleteCookie } from '../../../../tools/cookies';

export default class Auth extends BaseRequest {
    /**
     * Auth constructor
     *
     * @param {string} requestMode
     * @param {string} providerId
     * @param {string} customerId
     * @param {boolean} auth
     * @param {boolean} fowardRequest
     */
    constructor(
        requestMode = 'customer',
        providerId = '',
        customerId = '',
        auth = true,
        fowardRequest = false,
    ) {
        super(requestMode, providerId, customerId, auth, fowardRequest);
        this.setClassName('Auth');
        this.setEndpoint('auth');
    }

    /**
     * @param {string} email
     * @return {object}
     */
    async checkAccount(email = '') {
        let request = new HTTPRequest(
            'GET',
            `${this.getEndpoint()}/check_account`,
            this.getHeaders(),
            { email: email },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'checkAccount',
                { email: email },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {string} username
     * @param {string} password
     * @param {string} verificationKey
     * @return {object}
     */
    async login(username = '', password = '', verificationKey = '') {
        const request = this.buildForwardRequest(this.getClassName(), 'login', {
            username: username,
            password: password,
            verificationKey: verificationKey !== '' ? verificationKey : null,
        });

        return await request.sendRequest();
    }

    /**
     * @return {object}
     */
    logout() {
        deleteCookie('team-up-access-token');
        deleteCookie('team-up-refresh-token');
        deleteCookie('team-up-token-expires');
        deleteCookie('team-up-customer-id');
        deleteCookie('team-up-provider-id');
        deleteCookie('team-up-venue-id');
        deleteCookie('team-up-customer-first-name');
        deleteCookie('selected-studio-name');
        deleteCookie('selected-studio-id');

        return {
            results: [{ message: 'Successfully logged out' }],
            pagination: {
                page: 1,
                perPage: 20,
                total: 1,
                totalPages: 1,
            },
            errors: [],
            fieldErrors: [],
        };
    }

    /**
     * @param {string} providerId
     * @return {object}
     */
    async profile(providerId = '') {
        if (providerId == '') {
            providerId = this.getProviderId();
        }

        let profilesResponse = await this.profilesAll(20, ['provider']);

        if (profilesResponse['results'].length > 0) {
            profilesResponse['results'] = profilesResponse['results'].filter(
                (result) => result?.provider?.id == providerId,
            );
        }

        return profilesResponse;
    }

    /**
     * @param {integer} page
     * @param {integer} perPage
     * @param {array} expand
     * @param {array} fields
     * @param {boolean} fetchAll
     * @return {object}
     */
    async profiles(
        page = 1,
        perPage = 20,
        expand = [],
        fields = [],
        fetchAll = false,
    ) {
        let request = new HTTPRequest(
            'GET',
            `${this.getEndpoint()}/profiles`,
            this.getHeaders(),
            {
                expand: expand,
                fields: fields,
            },
            {},
            perPage,
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'profiles',
                {
                    page: page,
                    perPage: perPage,
                    expand: expand,
                    fields: fields,
                    fetchAll: fetchAll,
                },
            );
        }

        let profilesResponse = await request.sendRequestPaginated(
            page,
            fetchAll,
        );

        if (profilesResponse['results'].length > 0) {
            profilesResponse['results'] = profilesResponse['results'].filter(
                (result) => result?.profile_type == 'customer',
            );
        }

        return profilesResponse;
    }

    /**
     * @param {integer} perPage
     * @param {array} expand
     * @param {array} fields
     * @return {object}
     */
    async profilesAll(perPage = 20, expand = [], fields = []) {
        return await this.profiles(1, perPage, expand, fields, true);
    }

    /**
     * @return {object}
     */
    async applications() {
        let request = new HTTPRequest(
            'GET',
            `${this.getEndpoint()}/applications`,
            this.getHeaders(),
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'applications',
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {string} providerId
     * @return {object}
     */
    async check2faRequirements(providerId = '') {
        if (providerId != '') {
            this.setProviderId(providerId);
        }

        let request = new HTTPRequest(
            'GET',
            `${this.getEndpoint()}/two_factor/requirements`,
            this.getHeaders(),
            {provider: providerId}
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'check2faRequirements',
                {provider: providerId}
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {string} phoneNumber
     * @param {integer|null} pendingLogin
     * @param {string} providerId
     * @return {object}
     */
    async start2faVerification(phoneNumber = '', pendingLogin = null, providerId = '') {
        if (providerId != '') {
            this.setProviderId(providerId);
        }

        const request = this.buildForwardRequest(
            this.getClassName(), 
            'start2faVerification',
            {
                phoneNumber: phoneNumber,
                pendingLogin: pendingLogin,
                providerId: providerId,
            }
        );

        return await request.sendRequest();
    }

    /**
     * @param {string} phoneNumber
     * @param {string} verificationCode
     * @param {string} providerId
     * @return {object}
     */
    async verify2faCode(phoneNumber = '', verificationCode = '', providerId = '') {
        if (providerId != '') {
            this.setProviderId(providerId);
        }

        let request = new HTTPRequest(
            'POST',
            `${this.getEndpoint()}/two_factor/verify`,
            this.getHeaders(),
            {},
            {
                phone_number: phoneNumber,
                verification_code: verificationCode,
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'verify2faCode',
                {
                    phoneNumber: phoneNumber,
                    verificationCode: verificationCode,
                },
            );
        }

        return await request.sendRequest();
    }
}
